import { useState } from "react";
import { Copy, Check } from "lucide-react";

import { cn } from "@/lib/utils";

type CodeBlockProps = {
  value: string;
  className?: string;
  language?: string;
  collapsible?: boolean;
};

export function CodeBlock({ value, className, language, collapsible = false }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(!collapsible);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={cn("relative rounded-lg border border-border bg-background/40", className)}>
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
        <span>{language ? language.toUpperCase() : "JSON"}</span>
        <div className="flex items-center gap-2">
          {collapsible && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="text-primary hover:text-primary/80 transition-colors"
            >
              {expanded ? "Hide" : "Show"}
            </button>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/80 transition-all duration-200 hover:border-primary hover:text-foreground"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-success" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>
      {(!collapsible || expanded) && (
        <pre className="max-h-64 overflow-auto px-4 py-3 text-xs leading-relaxed text-foreground">
          <code>{value}</code>
        </pre>
      )}
    </div>
  );
}
